// WWI engineer workshop — a timber shop under a slate roof (the stone/mineral
// engineering code) filling the west half of the plot, an open repair yard on
// the east with an outdoor workbench (the "repair" station), an oversized cog
// raised on a post as the icon, and spare tank-track and girders in the yard.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, gable, slab, poles } = styleV3;

const TIMBER = "#8b6438";
const TIMBER_DK = "#5f451f";
const WOOD = "#9a6a38";
const GUNMETAL = "#4a4d52";
const STEEL = "#7d8288";
const SLATE = "#5e7287"; // the stone/mineral roof code

const BASE = 0.12;                      // foundation slab top
const BX = -0.45, BW = 0.95, BD = 1.5;  // the shop, west half of the plot
const WALL_H = 0.7;

// The timber shop walls, `h` tall on the foundation.
function shop(h: number): Part {
  return box(BW, h, BD, TIMBER, [BX, BASE + h / 2, 0]);
}

// The slate gable roof, ridge along z — ONE part, the mineral/engineering code.
function roof(): Part {
  return gable(BW + 0.14, BD + 0.16, 0.42, SLATE, BX, BASE + WALL_H, 0, "z");
}

// The gunmetal stove flue poking through the roof at the back.
function flue(h: number): Part {
  return cyl(0.055, 0.055, h, 6, GUNMETAL, [BX + 0.28, BASE + h / 2, -0.45]);
}

// The plank door on the shop front.
function door(): Part {
  return box(0.28, 0.5, 0.05, TIMBER_DK, [BX - 0.2, BASE + 0.25, BD / 2 + 0.02]);
}

// The outdoor workbench in the open yard, tools laid on top.
function bench(): Part[] {
  return [
    box(0.7, 0.1, 0.26, WOOD, [0.45, BASE + 0.38, 0.25]),
    box(0.7, 0.28, 0.06, WOOD, [0.45, BASE + 0.19, 0.34]),
    box(0.11, 0.1, 0.09, GUNMETAL, [0.68, BASE + 0.48, 0.25]),   // the vice
    box(0.2, 0.03, 0.05, STEEL, [0.38, BASE + 0.445, 0.22], [0, 0.5, 0]),
    cyl(0.02, 0.02, 0.16, 5, TIMBER_DK, [0.22, BASE + 0.44, 0.28], [0, 0, Math.PI / 2]),
  ];
}

// The OVERSIZED cog raised on a post at the yard corner — the icon from above.
function cogSign(): Part[] {
  const px = 0.62, pz = -0.6, top = BASE + 0.62;
  const out: Part[] = [
    cyl(0.03, 0.035, 0.6, 5, TIMBER, [px, BASE + 0.3, pz]),
    cyl(0.19, 0.19, 0.05, 8, GUNMETAL, [px, top, pz]),
    cyl(0.05, 0.05, 0.07, 6, STEEL, [px, top, pz]),
  ];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    out.push(box(0.05, 0.045, 0.05, GUNMETAL,
      [px + Math.cos(a) * 0.2, top, pz + Math.sin(a) * 0.2], [0, -a, 0]));
  }
  return out;
}

// A spare tank-track segment lying in the yard, cleats up.
function trackSegment(): Part[] {
  const out: Part[] = [box(0.55, 0.12, 0.2, GUNMETAL, [0.28, BASE + 0.06, -0.58], [0, 0.18, 0])];
  for (let i = 0; i < 4; i++) {
    out.push(box(0.035, 0.15, 0.21, "#3a3d41",
      [0.1 + i * 0.13, BASE + 0.065, -0.61 - (i - 1.5) * 0.023], [0, 0.18, 0]));
  }
  return out;
}

// Steel girders stacked along the yard's road edge.
function girders(): Part[] {
  return [
    box(0.9, 0.07, 0.09, STEEL, [0.42, BASE + 0.035, 0.75], [0, 0.06, 0]),
    box(0.85, 0.07, 0.09, STEEL, [0.4, BASE + 0.105, 0.73], [0, -0.05, 0]),
  ];
}

function foundation(): Part {
  return slab(1.94, 1.94);
}

export const engineerWorkshop = defineModel({
  id: "engineer-workshop",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Engineer Workshop",
  tags: ["building", "wwi", "military", "engineering", "workshop"],
  summary: "A timber workshop under a slate roof with an outdoor repair bench and spare parts in the yard.",
  description:
    "A Great-War engineer workshop: a timber shop under the slate engineering " +
    "roof, an open yard with an outdoor workbench where repairs are done, an " +
    "oversized cog raised on a post, and spare tank-track and girders waiting " +
    "in the yard.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [foundation()] },
    {
      id: "lower", label: "Lower",
      parts: () => [foundation(), shop(0.35), ...poles(1.7, 1.7, 0.9)],
    },
    {
      id: "upper", label: "Upper",
      parts: () => [foundation(), shop(WALL_H), door(), ...poles(1.7, 1.7, 1.3)],
    },
    {
      id: "complete", label: "Complete",
      parts: () => [
        foundation(), shop(WALL_H), roof(), flue(1.25), door(),
        ...bench(), ...cogSign(), ...trackSegment(), ...girders(),
      ],
    },
  ],
  animations: [],
  stations: [
    { id: "repair", position: [0.45, 0.12, 0.6], facing: Math.PI },
  ],
  interactions: [
    { id: "repair", label: "Repairing at the bench", station: "repair", variants: ["complete"], ...benchWork() },
  ],
});
